import { Stack, Switch, Typography, useTheme } from "@mui/material";
import { useAtomValue } from "jotai";
import { Bell, Globe, LogOut, Moon, Shield, Trash2 } from "lucide-react";
import type { FC } from "react";
import { useNavigate } from "react-router-dom";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import { getLanguageLabel } from "web_myplann/Infrastructure/Translations";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { LanguageDrawer } from "web_myplann/Profile/Components/LanguageDrawer";
import { ListRow } from "web_myplann/Shared/Components/ListRow";
import { SectionHeader } from "web_myplann/Shared/Components/SectionHeader";
import { MyPlannLayout } from "web_myplann/Shared/Header/MyPlannLayout";
import NavigationHeader from "web_myplann/Shared/Header/NavigationHeader";
import { useDarkModeToggle } from "web_myplann/Shared/Hooks/useDarkModeToggle";
import { useLanguagePicker } from "web_myplann/Shared/Hooks/useLanguagePicker";
import { useLogOut } from "web_myplann/Shared/Hooks/useLogOut";
import { appInfoState } from "web_myplann/Shared/State/appInfoAtom";

export const SettingsV2Page: FC = () => {
	const theme = useTheme();
	const { t } = useResource();
	const navigate = useNavigate();
	const appInfoAtom = useAtomValue(appInfoState);
	const { logOut, removeAccount } = useLogOut();
	const { isDarkMode, toggle: toggleDarkMode } = useDarkModeToggle();
	const {
		isOpen: isLanguageOpen,
		open: openLanguage,
		close: closeLanguage,
		select: selectLanguage,
	} = useLanguagePicker();

	return (
		<>
			<MyPlannLayout
				disableContentPadding
				bgColor={theme.palette.surface.muted}
				topBar={
					<NavigationHeader
						title={t(Resources.Settings.Items.Settings)}
						bgColor={theme.palette.background.paper}
						defaultRoute={Routing.Profile.More.path()}
					/>
				}
			>
				<Stack minHeight="100%">
					<SectionHeader title={t(Resources.Settings.Sections.Settings)} />
					<Stack bgcolor={theme.palette.background.paper}>
						<ListRow
							title={t(Resources.Settings.DarkMode)}
							icon={
								<Moon
									size={theme.iconSize.md}
									color={theme.palette.grey[500]}
								/>
							}
							iconBg={theme.palette.grey[100]}
							rightSlot={
								<Switch checked={isDarkMode} onChange={toggleDarkMode} />
							}
						/>
						<ListRow
							title={t(Resources.Settings.Language)}
							icon={
								<Globe
									size={theme.iconSize.md}
									color={theme.palette.grey[500]}
								/>
							}
							iconBg={theme.palette.grey[100]}
							rightText={t(getLanguageLabel())}
							onClick={openLanguage}
						/>
						<ListRow
							title={t(Resources.Settings.Items.Security)}
							icon={
								<Shield
									size={theme.iconSize.md}
									color={theme.palette.green[600]}
								/>
							}
							iconBg={theme.palette.green[100]}
							onClick={() => navigate(Routing.Profile.Security.path())}
						/>
						<ListRow
							title={t(Resources.Settings.Items.Notifications)}
							icon={
								<Bell
									size={theme.iconSize.md}
									color={theme.palette.blue[600]}
								/>
							}
							iconBg={theme.palette.blue[100]}
							isLast
							onClick={() => navigate(Routing.Notifications.path())}
						/>
					</Stack>

					<SectionHeader title={t(Resources.Settings.Sections.Account)} />
					<Stack bgcolor={theme.palette.background.paper}>
						<ListRow
							title={t(Resources.Settings.LogOut)}
							icon={
								<LogOut
									size={theme.iconSize.md}
									color={theme.palette.red[600]}
								/>
							}
							iconBg={theme.palette.red[100]}
							destructive
							showChevron={false}
							onClick={logOut}
						/>
						<ListRow
							title={t(Resources.Settings.RemoveAccount)}
							icon={
								<Trash2
									size={theme.iconSize.md}
									color={theme.palette.grey[500]}
								/>
							}
							iconBg={theme.palette.grey[100]}
							isLast
							showChevron={false}
							onClick={removeAccount}
						/>
					</Stack>

					<Stack alignItems="center" py={3}>
						<Typography variant="hint">
							{`${t(Resources.Settings.AppVersion)} ${appInfoAtom.appVersion}`}
						</Typography>
					</Stack>
				</Stack>
			</MyPlannLayout>

			<LanguageDrawer
				open={isLanguageOpen}
				onClose={closeLanguage}
				onSelect={selectLanguage}
			/>
		</>
	);
};
